import { useTextStream } from '@livekit/components-react';
import { useMemo } from 'react';

import { useStrings } from '../i18n/context';
import { COPILOT_TOPIC } from '../protocol';
import { latestCopilot } from '../streams';

interface Props {
  readonly open: boolean;
  /** Whether the interpreter is asked to run the copilot (vm.copilot); hiding the panel does not stop it. */
  readonly running: boolean;
  readonly agents: ReadonlySet<string>;
  readonly onHide: () => void;
  readonly onToggleRunning: () => void;
}

/**
 * The host's copilot (B-023): what the client wants so far, the checklist the agent keeps and
 * ticks, and the questions to ask now. Rendered for the host only; the interpreter sends the
 * state on vm.copilot to the host's identity alone. Stays mounted while hidden, so the latest
 * state is there when the host looks again (a text stream with no handler on its topic is
 * dropped). Hiding and stopping are two different buttons. Text is rendered as text.
 */
export function CopilotPanel({ open, running, agents, onHide, onToggleRunning }: Props) {
  const strings = useStrings();
  const { textStreams } = useTextStream(COPILOT_TOPIC);
  const state = useMemo(() => latestCopilot(textStreams, agents), [textStreams, agents]);

  return (
    <aside id="vm-copilot" className="notes copilot" aria-labelledby="vm-copilot-heading" hidden={!open}>
      <div className="notes__header">
        <h2 id="vm-copilot-heading">{strings.copilotHeading}</h2>
        <button type="button" className="button" aria-pressed={running} onClick={onToggleRunning}>
          {running ? strings.copilotStop : strings.copilotStart}
        </button>
        <button type="button" className="button" onClick={onHide}>
          {strings.copilotHide}
        </button>
      </div>
      {!running && <p className="notes__empty">{strings.copilotStopped}</p>}
      {state ? (
        <div className="notes__body" lang={state.language || undefined}>
          {state.summary && (
            <>
              <h3>{strings.copilotSummaryHeading}</h3>
              <p>{state.summary}</p>
            </>
          )}
          {state.checklist.length > 0 && (
            <>
              <h3>{strings.copilotChecklistHeading}</h3>
              <ul className="copilot__list">
                {state.checklist.map((item) => (
                  <li key={item.id} className={item.status === 'done' ? 'copilot__item--done' : undefined}>
                    <span aria-hidden="true">{item.status === 'done' ? '✓ ' : '○ '}</span>
                    <span className="visually-hidden">
                      {item.status === 'done' ? strings.copilotDone : strings.copilotOpen}:{' '}
                    </span>
                    <span>{item.item}</span>
                    {item.note && <span className="copilot__note">{item.note}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {state.suggestions.length > 0 && (
            <>
              <h3>{strings.copilotSuggestionsHeading}</h3>
              {state.suggestions.map((suggestion, index) => (
                // oxlint-disable-next-line react/no-array-index-key -- a fixed list from one state
                <p key={index} className="copilot__suggestion">
                  {suggestion}
                </p>
              ))}
            </>
          )}
        </div>
      ) : (
        running && <p className="notes__empty">{strings.copilotEmpty}</p>
      )}
    </aside>
  );
}
